import React from "react";

import {
	Button,
	InfoMessage,
	PasswordField,
	Input,
	Checkbox,
	Spinner,
} from "@amphore-dev/amphore-lib";
import { AxiosError } from "axios";
import { Form, Formik } from "formik";
import { exists, t } from "i18next";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { useMutation } from "@tanstack/react-query";

import AuthService from "@/services/AuthService";
import PasskeysService, { isCancelled } from "@/services/PasskeysService";
import UserService from "@/services/UserService";

import { useAuth } from "@/hooks/useAuth";

import { ConnectWithButton } from "@/components/atoms";

import { getApiErrormessage } from "@/utils/UAPIErrors";
import { fieldError } from "@/utils/UForms";
import { rememberReturnTo, returnToOf } from "@/utils/UReturnTo";
import { ucFirst } from "@/utils/UStrings";

import { CSSO_PROVIDERS_NAMES } from "@/constants/CSSO";

import { TAuthFormValues, TToken, TApiError } from "@/types";
import { TUser } from "@/types";

const initialValues: TAuthFormValues = {
	email: "",
	password: "",
	rememberMe: false,
};

const validationSchema = () =>
	Yup.object().shape({
		email: Yup.string().required(t("forms.required")),
		password: Yup.string().required(t("forms.required")),
	});

/**
 * The message for an SSO error code. A code this front has no words for
 * (an API newer than the build, a bug) says the generic thing rather
 * than showing its key.
 */
const ssoErrorKey = (code: string): string =>
	exists(`auth.errors.sso.${code}`)
		? `auth.errors.sso.${code}`
		: "auth.errors.sso.unexpected";

export const Auth: React.FC = () => {
	const { login } = useAuth();
	const navigate = useNavigate();
	// get location state
	const location = useLocation();
	const [SSOLogin, setSSOLogin] = React.useState(false);
	const state = (location.state || {}) as {
		error?: string;
		fromSSO?: boolean;
		provider?: string;
		authType?: string;
		email?: string;
		password?: string;
	};

	// The page the visitor was after, when the router sent them here:
	// remembered now (the submit clears the router state), GuestRoute
	// sends the signed-in user there. Holds across the SSO round trip too.
	const returnTo = returnToOf(location);
	React.useEffect(() => rememberReturnTo(returnTo), [returnTo]);

	const handleSubmit = async (values: TAuthFormValues) => {
		navigate(location.pathname, { replace: true });
		const token: TToken = await AuthService.login(values);
		const user = (await UserService.fetchMe(token.access_token)) as TUser;
		login(token, user);
		// GuestRoute takes over: the signed-in user leaves this page.
		return user;
	};

	// A passkey signs in on its own: no address, the device checks its owner.
	const passkeyMutation = useMutation<unknown, AxiosError<TApiError>>({
		mutationFn: async () => {
			try {
				const token: TToken = await PasskeysService.signIn();
				const user = (await UserService.fetchMe(
					token.access_token
				)) as TUser;
				login(token, user);
			} catch (error) {
				if (!isCancelled(error)) throw error;
			}
		},
	});

	const submitMutation = useMutation<
		unknown,
		AxiosError<TApiError>,
		TAuthFormValues
	>({
		mutationFn: handleSubmit,
	});

	if (SSOLogin) return <Spinner className="mx-auto w-full" size="lg" />;

	return (
		<>
			<Formik
				initialValues={{
					...initialValues,
					...state,
				}}
				onSubmit={(values) => submitMutation.mutateAsync(values)}
				validationSchema={validationSchema()}
			>
				{(formik) => {
					const { values, setFieldValue, isSubmitting } = formik;
					return (
						<Form className="flex flex-col gap-4">
							<div className="col gap-2">
								{state.error && state.fromSSO && (
									<InfoMessage color="danger">
										{t(ssoErrorKey(state.error), {
											provider:
												state.provider &&
												state.authType !== "internal"
													? t(
															"auth.errors.sso.with_provider",
															{
																provider:
																	ucFirst(
																		state.provider
																	),
															}
														)
													: t(
															"auth.errors.sso.with_password"
														),
										})}
									</InfoMessage>
								)}
								<Input
									label={t("forms.email.email")}
									name="email"
									error={fieldError(formik, "email")}
									type="email"
									autoComplete="email"
									disabled={isSubmitting}
									value={values.email}
									onChange={(value) => {
										setFieldValue("email", value);
									}}
									className="border-neutral-200"
								/>
								<PasswordField
									label={t("forms.password.password")}
									name="password"
									error={fieldError(formik, "password")}
									autoComplete="current-password"
									disabled={isSubmitting}
									value={values.password}
									onChange={(value) => {
										setFieldValue("password", value);
									}}
									className="border-neutral-200"
								/>
							</div>
							<div className="ml-4 text-sm text-neutral-600">
								<Checkbox
									label={t("auth.rememberMe")}
									name="rememberMe"
									checked={values.rememberMe}
									onChange={(checked) =>
										setFieldValue("rememberMe", checked)
									}
								/>
							</div>
							{!!submitMutation.error && (
								<InfoMessage color="danger">
									{getApiErrormessage(submitMutation.error)}
								</InfoMessage>
							)}
							<Button type="submit" isLoading={isSubmitting}>
								{t("auth.signIn")}
							</Button>
							<button
								type="button"
								className="text-sm text-neutral-500 hover:text-black"
								onClick={() =>
									navigate("/forgot-password", {
										state: { email: values.email },
									})
								}
							>
								{t("account.forgot.link")}
							</button>
						</Form>
					);
				}}
			</Formik>
			<hr className="my-4" />
			{PasskeysService.isSupported() && (
				<div className="mb-3 flex flex-col gap-2">
					<Button
						variant="outline"
						picto="lock"
						isLoading={passkeyMutation.isPending}
						onClick={() => passkeyMutation.mutate()}
						data-testid="passkey-sign-in"
					>
						{t("account.passkeys.signIn")}
					</Button>
					{!!passkeyMutation.error && (
						<InfoMessage color="danger">
							{getApiErrormessage(passkeyMutation.error)}
						</InfoMessage>
					)}
				</div>
			)}
			<div className="flex flex-wrap justify-center gap-2">
				{CSSO_PROVIDERS_NAMES.map((provider) => (
					<div key={provider} className="flex justify-center">
						<ConnectWithButton
							provider={provider}
							onClick={() => setSSOLogin(true)}
							disabled={SSOLogin}
						/>
					</div>
				))}
			</div>
			<hr className="my-4" />
			<div className="flex justify-center text-neutral-500 hover:*:text-black">
				<button type="button" onClick={() => navigate("/register")}>
					{t("register.register")}
				</button>
			</div>
		</>
	);
};
